import type { NodePropertySchema } from '../../../types/node-properties';

/**
 * Brave Search Node Schema
 * Web search using Brave Search API with advanced filtering
 */
const schema: NodePropertySchema = {
  nodeType: 'brave-search',
  version: '1.0.0',
  
  properties: [
    // ==================================================================
    // AUTHENTICATION
    // ==================================================================
    {
      key: 'credentialId',
      label: 'API Credential',
      type: 'credential',
      credentialTypes: [{ name: 'api_key' }],
      required: true,
      description: 'Brave Search API key credential',
      validation: [
        {
          type: 'required',
          message: 'API credential is required'
        }
      ]
    },
    
    // ==================================================================
    // CORE SEARCH PARAMETERS
    // ==================================================================
    {
      key: 'query',
      label: 'Search Query',
      type: 'string',
      default: '',
      placeholder: 'Enter search query...',
      required: true,
      description: 'The search query (max 400 characters, 50 words)',
      validation: [
        {
          type: 'required',
          message: 'Search query is required'
        }
      ]
    },
    
    {
      key: 'count',
      label: 'Result Count',
      type: 'number',
      default: 10,
      description: 'Number of results to return (1-20)',
      validation: [
        {
          type: 'min',
          value: 1,
          message: 'Count must be at least 1'
        },
        {
          type: 'max',
          value: 20,
          message: 'Count cannot exceed 20'
        }
      ]
    },
    
    {
      key: 'country',
      label: 'Country',
      type: 'options',
      options: [
        { name: 'United States', value: 'US' },
        { name: 'United Kingdom', value: 'GB' },
        { name: 'Canada', value: 'CA' },
        { name: 'Australia', value: 'AU' },
        { name: 'Germany', value: 'DE' },
        { name: 'France', value: 'FR' },
        { name: 'Spain', value: 'ES' },
        { name: 'Italy', value: 'IT' },
        { name: 'Netherlands', value: 'NL' },
        { name: 'Brazil', value: 'BR' },
        { name: 'India', value: 'IN' },
        { name: 'Japan', value: 'JP' },
        { name: 'China', value: 'CN' },
        { name: 'South Korea', value: 'KR' },
        { name: 'All Countries', value: 'ALL' }
      ],
      default: 'US',
      description: 'Country for localized search results'
    },
    
    {
      key: 'safesearch',
      label: 'Safe Search',
      type: 'options',
      options: [
        { name: 'Off', value: 'off' },
        { name: 'Moderate', value: 'moderate' },
        { name: 'Strict', value: 'strict' }
      ],
      default: 'moderate',
      description: 'Filter explicit content from results'
    },
    
    {
      key: 'freshness',
      label: 'Freshness',
      type: 'options',
      options: [
        { name: 'Any Time', value: 'any' },
        { name: 'Past Day', value: 'day' },
        { name: 'Past Week', value: 'week' },
        { name: 'Past Month', value: 'month' },
        { name: 'Past Year', value: 'year' }
      ],
      default: 'any',
      description: 'Filter results by recency'
    },
    
    // ==================================================================
    // ADVANCED OPTIONS
    // ==================================================================
    {
      key: 'search_lang',
      label: 'Search Language',
      type: 'options',
      options: [
        { name: 'English', value: 'en' },
        { name: 'Spanish', value: 'es' },
        { name: 'French', value: 'fr' },
        { name: 'German', value: 'de' },
        { name: 'Italian', value: 'it' },
        { name: 'Portuguese', value: 'pt' },
        { name: 'Dutch', value: 'nl' },
        { name: 'Japanese', value: 'ja' },
        { name: 'Korean', value: 'ko' },
        { name: 'Chinese (Simplified)', value: 'zh-hans' },
        { name: 'Chinese (Traditional)', value: 'zh-hant' },
        { name: 'Russian', value: 'ru' }
      ],
      default: 'en',
      description: 'Language for search interface and results'
    },
    
    {
      key: 'offset',
      label: 'Offset',
      type: 'number',
      default: 0,
      description: 'Pagination offset for results (0-9)',
      displayOptions: {
        show: {
          count: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20]
        }
      },
      validation: [
        {
          type: 'min',
          value: 0,
          message: 'Offset must be at least 0'
        },
        {
          type: 'max',
          value: 9,
          message: 'Offset cannot exceed 9'
        }
      ]
    },
    
    {
      key: 'spellcheck',
      label: 'Spell Check',
      type: 'boolean',
      default: true,
      description: 'Enable automatic spell checking and query correction'
    },
    
    {
      key: 'text_decorations',
      label: 'Text Decorations',
      type: 'boolean',
      default: true,
      description: 'Include text decorations (highlighting) in results'
    },
    
    {
      key: 'result_filter',
      label: 'Result Types',
      type: 'multiOptions',
      options: [
        { name: 'Web', value: 'web' },
        { name: 'News', value: 'news' },
        { name: 'Videos', value: 'videos' },
        { name: 'Images', value: 'images' }
      ],
      default: [],
      description: 'Filter results by content type (empty = all types)'
    }
  ]
};

export default schema;
